import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Paper, Grid, IconButton, LinearProgress } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import TimerIcon from '@mui/icons-material/Timer';
import StraightenIcon from '@mui/icons-material/Straighten';
import PaceIcon from '@mui/icons-material/Speed';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

const RunningTracker = ({ route, userLocation, onStartTracking, onStopTracking, onUpdatePosition, isMobile }) => {
    const { t } = useTranslation();
    const [isActive, setIsActive] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [distanceCovered, setDistanceCovered] = useState(0);
    const [lastPosition, setLastPosition] = useState(null);
    const [distanceRemaining, setDistanceRemaining] = useState(null);
    const [pace, setPace] = useState(0); // min/km
    const timerRef = useRef(null);
    const wakeLockRef = useRef(null);

    // Screen Wake Lock kezelése
    useEffect(() => {
        const requestWakeLock = async () => {
            if ('wakeLock' in navigator) {
                try {
                    wakeLockRef.current = await navigator.wakeLock.request('screen');
                    
                    wakeLockRef.current.addEventListener('release', () => {
                    });
                } catch (err) {
                    console.error(`${err.name}, ${err.message}`);
                }
            }
        };

        const releaseWakeLock = async () => {
            if (wakeLockRef.current !== null) {
                try {
                    await wakeLockRef.current.release();
                    wakeLockRef.current = null;
                } catch (err) {
                    console.error(`${err.name}, ${err.message}`);
                }
            }
        };

        if (isActive) {
            requestWakeLock();
        } else {
            releaseWakeLock();
        }

        // Ha a tab láthatósága változik (pl. háttérbe kerül), próbáljuk meg újra kérni
        const handleVisibilityChange = async () => {
            if (isActive && wakeLockRef.current !== null && document.visibilityState === 'visible') {
                await requestWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            releaseWakeLock();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isActive]);

    // Szólunk a szülőnek, ha változik az aktivitás állapota
    useEffect(() => {
        if (isActive) {
            if (onStartTracking) onStartTracking();
        } else {
            if (onStopTracking) onStopTracking();
        }
    }, [isActive, onStartTracking, onStopTracking]);

    // Ha a route vagy userLocation változik, számoljuk ki a hátralévő távot
    useEffect(() => {
        if (route && userLocation && isActive) {
            calculateRemainingDistance(userLocation);
        }
    }, [route, userLocation, isActive]);

    useEffect(() => {
        if (isActive) {
            timerRef.current = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [isActive]);

    useEffect(() => {
        if (isActive && userLocation) {
            const currentLatLng = L.latLng(userLocation.lat, userLocation.lng);
            if (lastPosition) {
                const lastLatLng = L.latLng(lastPosition.lat, lastPosition.lng);
                const dist = lastLatLng.distanceTo(currentLatLng);
                if (dist > 5) { // Csak ha 5 méternél többet mozdult (zajszűrés)
                    setDistanceCovered(prev => prev + dist);
                    setLastPosition(userLocation);
                }
            } else {
                setLastPosition(userLocation);
            }
        }
    }, [userLocation, isActive, lastPosition]);

    const calculateRemainingDistance = (currentLoc) => {
        if (!route || !route.geometry) return;
        
        const type = route.geometry.type;
        if (type !== 'MultiLineString' && type !== 'LineString') return;

        const currentLatLng = L.latLng(currentLoc.lat, currentLoc.lng);
        const lines = type === 'MultiLineString' ? route.geometry.coordinates : [route.geometry.coordinates];
        
        // Keressük meg a legközelebbi pontot a nyomvonalon
        let minDistanceToRoute = Infinity;
        let closestPointIndex = -1;
        let closestLineIndex = -1;

        lines.forEach((line, lineIdx) => {
            line.forEach((coord, pointIdx) => {
                const routePt = L.latLng(coord[1], coord[0]);
                const d = currentLatLng.distanceTo(routePt);
                if (d < minDistanceToRoute) {
                    minDistanceToRoute = d;
                    closestPointIndex = pointIdx;
                    closestLineIndex = lineIdx;
                }
            });
        });

        // Számoljuk ki a hátralévő távolságot a legközelebbi ponttól a végéig
        let remaining = 0;
        // Csak az aktuális vonalon számolunk a végétől visszafelé a legközelebbi pontig
        const currentLine = lines[closestLineIndex];
        for (let i = closestPointIndex; i < currentLine.length - 1; i++) {
            const p1 = L.latLng(currentLine[i][1], currentLine[i][0]);
            const p2 = L.latLng(currentLine[i+1][1], currentLine[i+1][0]);
            remaining += p1.distanceTo(p2);
        }
        
        // Ha van több vonal a MultiLineString-ben, addjuk hozzá a többi teljes hosszát
        for (let i = closestLineIndex + 1; i < lines.length; i++) {
            const line = lines[i];
            for (let j = 0; j < line.length - 1; j++) {
                const p1 = L.latLng(line[j][1], line[j][0]);
                const p2 = L.latLng(line[j+1][1], line[j+1][0]);
                remaining += p1.distanceTo(p2);
            }
        }

        setDistanceRemaining(remaining / 1000); // km

        // Pace számítása
        if (distanceCovered > 0.01) {
            const currentPace = (elapsedTime / 60) / (distanceCovered / 1000);
            setPace(currentPace);
        }
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return [h, m, s].map(v => v < 10 ? "0" + v : v).filter((v, i) => v !== "00" || i > 0).join(":");
    };

    const formatPace = (paceMinKm) => {
        if (!paceMinKm || isFinite(paceMinKm) === false || paceMinKm === 0) return "0:00";
        const min = Math.floor(paceMinKm);
        const sec = Math.round((paceMinKm - min) * 60);
        return `${min}:${sec < 10 ? '0' + sec : sec}`;
    };

    const handleStart = () => {
        setIsActive(true);
        if (!startTime) {
            setStartTime(new Date());
            setLastPosition(userLocation);
            if (userLocation) {
                calculateRemainingDistance(userLocation);
            }
        }
    };

    const handleStop = () => {
        setIsActive(false);
    };

    const handleClear = () => {
        setIsActive(false);
        setStartTime(null);
        setElapsedTime(0);
        setDistanceCovered(0);
        setLastPosition(null);
        setPace(0);
        setDistanceRemaining(null);
    };

    const estimatedTimeRemaining = () => {
        if (pace > 0 && distanceRemaining > 0) {
            return pace * distanceRemaining * 60; // másodpercben
        }
        return null;
    };

    return (
        <Paper elevation={isMobile ? 0 : 3} sx={{ p: isMobile ? 0 : 2, mt: isMobile ? 0 : 2, bgcolor: isMobile ? 'transparent' : '#f5f5f5', borderRadius: 2, boxShadow: isMobile ? 'none' : 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DirectionsRunIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {t('Running.Tracker.Title', 'Futás követése')}
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimerIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{t('Running.Tracker.Time', 'Idő')}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>{formatTime(elapsedTime)}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StraightenIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{t('Running.Tracker.Distance', 'Távolság')}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>{(distanceCovered / 1000).toFixed(2)} km</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PaceIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{t('Running.Tracker.Pace', 'Pace')}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>{formatPace(pace)} min/km</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StraightenIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{t('Running.Tracker.Remaining', 'Hátralévő')}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>{distanceRemaining ? distanceRemaining.toFixed(2) : "0.00"} km</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DirectionsRunIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{t('Running.Tracker.Speed', 'Sebesség')}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>{userLocation?.speed ? (userLocation.speed * 3.6).toFixed(1) : "0.0"} km/h</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DirectionsRunIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{t('Running.Tracker.Altitude', 'Magasság')}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>{userLocation?.altitude ? Math.round(userLocation.altitude) : "---"} m</Typography>
                </Grid>
            </Grid>

            {distanceRemaining !== null && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {t('Running.Tracker.EstimatedTime', 'Becsült hátralévő idő')}: {estimatedTimeRemaining() ? formatTime(Math.round(estimatedTimeRemaining())) : '--:--'}
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={route.properties.distance ? Math.min(100, (distanceCovered / (route.properties.distance * 1000)) * 100) : 0} 
                        sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
                    />
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                {!isActive ? (
                    <Button 
                        variant="contained" 
                        color="success" 
                        startIcon={<PlayArrowIcon />} 
                        onClick={handleStart}
                        sx={{ minWidth: '100px' }}
                    >
                        {t('Running.Tracker.Start', 'Indítás')}
                    </Button>
                ) : (
                    <Button 
                        variant="contained" 
                        color="warning" 
                        startIcon={<StopIcon />} 
                        onClick={handleStop}
                        sx={{ minWidth: '100px' }}
                    >
                        {t('Running.Tracker.Stop', 'Megállítás')}
                    </Button>
                )}
                <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<DeleteSweepIcon />} 
                    onClick={handleClear}
                    sx={{ flexShrink: 0 }}
                >
                    {t('Common.Clear', 'Eldobás')}
                </Button>
            </Box>
        </Paper>
    );
};

export default RunningTracker;
